// src/components/SenderBriefing/SenderBriefing.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  BookOpen,
  X,
  Download,
  CheckCircle2,
  Package,
  ArrowRight,
} from 'lucide-react';
import { updateUserProfile } from '../../firebase/user';

const SENDER_GUIDE_URL = '/sender-guide.pdf';

export const SenderBriefing = ({ user, userData }) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [open,      setOpen]      = useState(false);
  const [marking,   setMarking]   = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isSender = userData?.mode === 'sender';
  const hasRead  = !!userData?.senderBriefingRead;

  if (!isSender || hasRead || dismissed) return null;

  const handleOpen  = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleFinish = async () => {
    setMarking(true);
    try {
      if (user?.uid) await updateUserProfile(user.uid, { senderBriefingRead: true });
      setOpen(false);
      setDismissed(true);
    } catch (err) {
      console.error('Failed to mark sender briefing as read:', err);
      setOpen(false);
      setDismissed(true);
    } finally {
      setMarking(false);
    }
  };

  const handleDownload = () => {
    window.open(SENDER_GUIDE_URL, '_blank', 'noopener,noreferrer');
  };

  const color  = '#2563eb';
  const isDark = theme.palette.mode === 'dark';
  const bg     = isDark ? '#172554' : '#eff6ff';

  return (
    <>
      {/* ── Floating pill banner ─────────────────────────────────────────── */}
      <Box
        onClick={handleOpen}
        role="button"
        aria-label="Avaa lähettäjän opas"
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (t) => t.zIndex.appBar - 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.25,
          bgcolor: isDark ? '#1e3a6e' : '#1e3a6e',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: '999px',
          px: 1.25,
          py: 1,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.93 },
        }}
      >
        {/* Icon circle */}
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%',
          bgcolor: 'rgba(147,197,253,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <BookOpen size={13} color="#93c5fd" />
        </Box>

        {/* Label */}
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: '#bfdbfe', letterSpacing: 0.2, fontSize: '0.75rem' }}
        >
          Uusi lähettäjä? Lataa opas
        </Typography>

        {/* Inner CTA pill */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          bgcolor: color, borderRadius: '999px',
          px: 1.25, py: 0.4, flexShrink: 0,
        }}>
          <Typography variant="caption" fontWeight={700} sx={{ color: '#fff', fontSize: '0.7rem' }}>
            Avaa
          </Typography>
          <ArrowRight size={11} color="#fff" />
        </Box>
      </Box>

      {/* ── Modal — centered ─────────────────────────────────────────────── */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: isMobile ? 0 : '20px',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          },
        }}
      >
        {/* ── Header ── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, pt: 2, pb: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BookOpen size={16} />
            <Typography variant="subtitle2" fontWeight={700}>Lähettäjän opas</Typography>
          </Stack>
          <IconButton size="small" onClick={handleClose} sx={{ borderRadius: '8px' }}>
            <X size={16} />
          </IconButton>
        </Box>

        {/* ── Body ── */}
        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 3 }}>
          <Stack spacing={2.5}>

            {/* Icon + title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
                bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid', borderColor: alpha(color, 0.2),
              }}>
                <Package size={24} color={color} strokeWidth={1.75} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25 }}>
                Tervetuloa Pakettihuoltoon!
              </Typography>
            </Box>

            {/* Intro */}
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Olemme koonneet lyhyen oppaan, joka kertoo miten lähetyksen tekeminen onnistuu.
            </Typography>

            {/* Download card */}
            <Box sx={{
              p: 2, borderRadius: '12px',
              border: '1px solid', borderColor: alpha(color, 0.25),
              bgcolor: alpha(color, 0.04),
              display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
                bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Download size={16} color={color} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700}>Lähettäjän opas (PDF)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pakettihuolto · Pikaohje lähettäjälle
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleDownload}
                startIcon={<Download size={14} />}
                sx={{
                  borderRadius: '8px', textTransform: 'none',
                  fontWeight: 700, boxShadow: 'none', flexShrink: 0,
                  bgcolor: color,
                  '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)', bgcolor: color },
                }}
              >
                Lataa
              </Button>
            </Box>

          </Stack>
        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="small"
            endIcon={<CheckCircle2 size={15} />}
            onClick={handleFinish}
            disabled={marking}
            sx={{
              borderRadius: '10px', textTransform: 'none',
              fontWeight: 700, boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Selvä, jatketaan!
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SenderBriefing;